import { forwardRef, HTMLProps, useRef } from 'react'
import clsx from 'classnames'
import { useTranslations } from 'next-intl'
import { EventCard } from '@/entities/event-card'
import { IEventPreview } from '@/interfaces/events'
import { useIntersection } from '@/shared/utils'
import { Text } from '@/shared/ui'
import { EventsLoader } from './events-loader'
import style from './events-async-list.module.scss'

interface EventsListProps extends HTMLProps<HTMLUListElement> {
  cards: IEventPreview[]
  publications?: boolean
  anchorId?: string
  onCurrentPage?: (page: number) => void
  onFetchPage?: (page: number) => void
}

export const EventsAsyncList = forwardRef<HTMLUListElement, EventsListProps>((props, ref) => {
  const { cards, anchorId, publications, onCurrentPage, onFetchPage, ...restProps } = props
  const listRef = useRef<HTMLUListElement>(null)
  const translate = useTranslations()

  useIntersection({
    ref: listRef,
    onCurrentPage: page => {
      onCurrentPage?.(page)
    },
    options: {
      rootMargin: '-15% 0px -80% 0px',
    },
  })
  useIntersection({
    ref: listRef,
    onCurrentPage: (page, observerInstance) => {
      if (cards?.length) {
        observerInstance.disconnect()
      } else {
        onFetchPage?.(page)
      }
    },
    options: {
      rootMargin: '160% 0px 70% 0px',
    },
  })

  if (cards?.length === 0)
    return (
      <div className={style.empty}>
        <Text type="h1" color="dark">
          {translate('eventsNotFound')}
        </Text>
      </div>
    )

  return (
    <ul
      id={anchorId}
      style={cards ? {} : { minHeight: '110vh' }}
      ref={el => {
        // @ts-ignore
        ref?.(el)
        // @ts-ignore
        listRef.current = el
      }}
      className={style.container}
      {...restProps}
    >
      {cards ? (
        cards.map(eventProps => <EventCard className={style.item} key={eventProps.id} {...eventProps} />)
      ) : (
        <EventsLoader className={style.item} />
      )}
    </ul>
  )
})

EventsAsyncList.displayName = 'EventsAsyncList'
